import React, { useRef } from "react";
import "./Home.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCode,
  faEnvelope,
  faPhone,
  faDatabase,
  faCloud,
  faFire,
  faBolt,
  faWind,
  faMobile,
  faCircle,
  faExternalLinkAlt,
  faBrain,
} from "@fortawesome/free-solid-svg-icons";
import {
  faLinkedin,
  faGithub,
  faWhatsapp,
  faFacebook,
  faInstagram,
  faXTwitter,
  faHtml5,
  faCss3Alt,
  faJs,
  faReact,
  faPython,
  faGitAlt,
} from "@fortawesome/free-brands-svg-icons";
import { faAndroid, faYarn } from "@fortawesome/free-brands-svg-icons";

import {
  faTerminal,
  faMicrochip,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";

const ProjectCard = ({
  name,
  logo,
  description,
  features = [],
  techStack = [],
  links = {},
}) => {
  return (
    <div className="project-card-container">
      <div className="project-expandable-card">
        <div className="project-header">
          {logo && (
            <img src={logo} alt={`${name} logo`} className="project-logo" />
          )}
          <h3 className="project-name">{name}</h3>
        </div>

        <div className="expanded-content">
          <div className="project-details">
            <p className="project-description">{description}</p>
            <ul className="project-features">
              {features.map((feature, index) => (
                <li key={index}>
                  <FontAwesomeIcon icon={faCircle} className="feature-bullet" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <div className="project-links">
              {links.live && (
                <a
                  href={links.live}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link"
                >
                  <FontAwesomeIcon
                    icon={faExternalLinkAlt}
                    style={{ marginRight: "8px" }}
                  />
                  Live Demo
                </a>
              )}
              {links.github && (
                <a
                  href={links.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-link"
                >
                  <FontAwesomeIcon
                    icon={faGithub}
                    style={{ marginRight: "8px" }}
                  />
                  GitHub
                </a>
              )}
            </div>
          </div>

          <div className="project-tech">
            <h4 className="tech-title">Tech Stack</h4>
            <div className="tech-grid">
              {techStack.map((tech, index) => (
                <div key={index} className="tech-item">
                  <FontAwesomeIcon icon={tech.icon} className="tech-icon" />
                  <span className="tech-name">{tech.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Home() {
  const aboutRef = useRef(null);
  const skillsRef = useRef(null);

  return (
    <div className="home">
      {/* PAGE HEADER (Formerly Navbar) */}
      <header className="page-header">
        <div className="header-logo">
          <a href="/" className="logo-link">
            Made by Angel
          </a>
        </div>
        <div className="header-socials">
          <a href="#contact" className="hire-me-link">
            Hire Me
          </a>
          <a
            href="https://github.com/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="header-social-link"
          >
            <FontAwesomeIcon icon={faGithub} />
          </a>
          <a
            href="https://www.linkedin.com/in/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="header-social-link"
          >
            <FontAwesomeIcon icon={faLinkedin} />
          </a>
        </div>
      </header>

      {/* ABOUT ME */}
      <section className="about succession-scroll" id="about" ref={aboutRef}>
        <div className="about-inner">
          <div className="about-text">
            <h2>About Me</h2>
            <p className="about-subtitle">
              Developer. Builder. Future Founder.
            </p>
            <p className="about-bio-text">
              Hi, I'm Angel Phiri. I build software, explore ideas, and create
              digital products. My interests span full-stack development,
              artificial intelligence, product design, and mobile systems. I
              enjoy transforming complex problems into simple, elegant
              solutions—from developing scalable applications to experimenting
              with innovative concepts that improve how people work, learn, and
              connect. Driven by curiosity and continuous learning, I'm always
              looking for the next challenge and the next opportunity to build
              something meaningful.
            </p>
            <div className="about-tags">
              <span className="about-tag">Software Development</span>
              <span className="about-tag">Mobile Systems</span>
              <span className="about-tag">Android Modding</span>
              <span className="about-tag">UI / UX</span>
              <span className="about-tag">Tech Enthusiast</span>
            </div>
            <div className="about-stats-row">
              <div className="about-stat-card">
                <span className="about-stat-number">4+</span>
                <span className="about-stat-label">Projects Shipped</span>
              </div>
              <div className="about-stat-card">
                <span className="about-stat-number">5+</span>
                <span className="about-stat-label">Years of Experience</span>
              </div>
            </div>
          </div>

          <div className="about-image-side">
            <div className="about-image-frame">
              <img src={require("../assets/myimage.png")} alt="Angel" />
            </div>
          </div>
        </div>

        {/* SKILLS inside About */}
        <div className="skills-section" ref={skillsRef}>
          <h3>Skills</h3>

          <div className="skills-columns">
            {/* LEFT COLUMN */}
            <div className="skills-column">
              <div className="skills-category">
                <h4>Languages</h4>

                <div className="skills-grid">
                  <div className="skill-flat-card html">
                    <FontAwesomeIcon
                      icon={faHtml5}
                      className="skill-flat-icon"
                    />
                    <h5>HTML</h5>
                  </div>

                  <div className="skill-flat-card css">
                    <FontAwesomeIcon
                      icon={faCss3Alt}
                      className="skill-flat-icon"
                    />
                    <h5>CSS</h5>
                  </div>

                  <div className="skill-flat-card js">
                    <FontAwesomeIcon icon={faJs} className="skill-flat-icon" />
                    <h5>JavaScript</h5>
                  </div>

                  <div className="skill-flat-card ts">
                    <span className="skill-flat-text-icon">TS</span>
                    <h5>TypeScript</h5>
                  </div>

                  <div className="skill-flat-card python">
                    <FontAwesomeIcon
                      icon={faPython}
                      className="skill-flat-icon"
                    />
                    <h5>Python</h5>
                  </div>
                </div>
              </div>

              <div className="skills-category">
                <h4>Frontend</h4>

                <div className="skills-grid">
                  <div className="skill-flat-card react">
                    <FontAwesomeIcon
                      icon={faReact}
                      className="skill-flat-icon"
                    />
                    <h5>React</h5>
                  </div>

                  <div className="skill-flat-card react">
                    <FontAwesomeIcon
                      icon={faReact}
                      className="skill-flat-icon"
                    />
                    <h5>React Native</h5>
                  </div>

                  <div className="skill-flat-card vite">
                    <FontAwesomeIcon
                      icon={faBolt}
                      className="skill-flat-icon"
                    />
                    <h5>Vite</h5>
                  </div>

                  <div className="skill-flat-card tailwind">
                    <FontAwesomeIcon
                      icon={faWind}
                      className="skill-flat-icon"
                    />
                    <h5>Tailwind CSS</h5>
                  </div>

                  <div className="skill-flat-card shadcn">
                    <span className="skill-flat-text-icon">UI</span>
                    <h5>shadcn/ui</h5>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div className="skills-column">
              <div className="skills-category">
                <h4>Mobile / Android</h4>

                <div className="skills-grid">
                  <div className="skill-flat-card expo">
                    <FontAwesomeIcon
                      icon={faMobile}
                      className="skill-flat-icon"
                    />
                    <h5>Expo</h5>
                  </div>

                  <div className="skill-flat-card android">
                    <FontAwesomeIcon
                      icon={faAndroid}
                      className="skill-flat-icon"
                    />
                    <h5>Android Studio</h5>
                  </div>

                  <div className="skill-flat-card adb">
                    <FontAwesomeIcon
                      icon={faTerminal}
                      className="skill-flat-icon"
                    />
                    <h5>ADB / Fastboot</h5>
                  </div>

                  <div className="skill-flat-card rom">
                    <FontAwesomeIcon
                      icon={faMicrochip}
                      className="skill-flat-icon"
                    />
                    <h5>Custom ROM</h5>
                  </div>

                  <div className="skill-flat-card kernel">
                    <FontAwesomeIcon
                      icon={faLayerGroup}
                      className="skill-flat-icon"
                    />
                    <h5>Kernel Modding</h5>
                  </div>
                </div>
              </div>

              <div className="skills-category">
                <h4>Tools & Cloud</h4>

                <div className="skills-grid">
                  <div className="skill-flat-card git">
                    <FontAwesomeIcon
                      icon={faGitAlt}
                      className="skill-flat-icon"
                    />
                    <h5>Git</h5>
                  </div>

                  <div className="skill-flat-card github">
                    <FontAwesomeIcon
                      icon={faGithub}
                      className="skill-flat-icon"
                    />
                    <h5>GitHub</h5>
                  </div>

                  <div className="skill-flat-card vscode">
                    <FontAwesomeIcon
                      icon={faCode}
                      className="skill-flat-icon"
                    />
                    <h5>VS Code</h5>
                  </div>

                  <div className="skill-flat-card firebase">
                    <FontAwesomeIcon
                      icon={faFire}
                      className="skill-flat-icon"
                    />
                    <h5>Firebase Auth</h5>
                  </div>

                  <div className="skill-flat-card firestore">
                    <FontAwesomeIcon
                      icon={faDatabase}
                      className="skill-flat-icon"
                    />
                    <h5>Firestore</h5>
                  </div>

                  <div className="skill-flat-card gcloud">
                    <FontAwesomeIcon
                      icon={faCloud}
                      className="skill-flat-icon"
                    />
                    <h5>Google Cloud</h5>
                  </div>

                  <div className="skill-flat-card yarn">
                    <FontAwesomeIcon
                      icon={faYarn}
                      className="skill-flat-icon"
                    />
                    <h5>Yarn</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* PROJECTS */}
      <section className="projects" id="projects">
        <h2>Projects</h2>
        <div className="projects-list">
          <ProjectCard
            name="Learnr"
            description="A modern productivity web app designed to help students plan, organize, and track their learning tasks efficiently. Combines task management, calendar planning, and progress tracking in one clean, student-focused platform."
            features={[
              "Task and goal management",
              "Custom learning roadmaps",
              "Firebase authentication & profiles",
              "Progress tracking dashboards",
              "Responsive UI with dark/light mode",
            ]}
            techStack={[
              { name: "React (Vite)", icon: faReact },
              { name: "Tailwind CSS", icon: faWind },
              { name: "React Hooks", icon: faCode },
            ]}
            links={{
              live: "https://luanr.netlify.app/",
              github: "https://github.com/4anglais/learnr2.0",
            }}
          />
          <ProjectCard
            name="NexaPayslip"
            description="A full-stack payroll platform built for small businesses in Zambia. Covers the complete payroll workflow — employee records, statutory rate configuration, payroll runs, payslip generation, and payslip verification — across both a web app and a native mobile app."
            features={[
              "Employee records management",
              "Statutory rate configuration",
              "Payroll runs and payslip generation",
              "Payslip verification",
              "Cross-platform support (web and mobile)",
            ]}
            techStack={[
              { name: "React 19", icon: faReact },
              { name: "TypeScript", icon: faCode },
              { name: "Vite", icon: faBolt },
              { name: "Firebase", icon: faFire },
              { name: "Expo SDK", icon: faMobile },
            ]}
            links={{
              live: "https://nexapayroll.vercel.app/",
              github: "",
            }}
          />
          <ProjectCard
            name="Cashly"
            description="A clean, minimal personal finance tracker built for simplicity and clarity."
            features={[
              "Expense tracking",
              "Budget management",
              "Financial insights",
              "User-friendly interface",
            ]}
            techStack={[
              { name: "React", icon: faReact },
              { name: "TypeScript", icon: faCode },
              { name: "CSS", icon: faCss3Alt },
            ]}
            links={{
              live: "https://usecashly.web.app/",
              github: "",
            }}
          />
          <ProjectCard
            name="Reebase"
            description="REEBASE is an AI-powered idea intelligence engine with two entry points: The Failed Startup Directory Search any company that failed — Quibi, Theranos, Vine, MoviePass, any of them."
            features={[
              "AI-powered idea intelligence",
              "Failed startup directory",
              "Comprehensive search functionality",
              "User-friendly interface",
            ]}
            techStack={[
              { name: "Build-in DB", icon: faDatabase },
              { name: "GROQ", icon: faCode },
              { name: "Llama AI", icon: faBrain },
              { name: "Medo", icon: faCode },
              { name: "Node.js", icon: faJs },
              { name: "React", icon: faReact },
              { name: "REST API", icon: faCode },
              { name: "Tailwind", icon: faWind },
              { name: "TypeScript", icon: faCode },
              { name: "Wikipedia API", icon: faCode },
            ]}
            links={{
              live: "",
              github: "",
            }}
          />
        </div>
      </section>

      {/* CONTACT */}
      <section className="contact" id="contact">
        <h2>Contact Me</h2>
        <p className="contact-subtitle">
          Get in touch with me through any of these channels
        </p>

        <div className="contact-grid">
          <a
            href="mailto:angelphiri.2021@gmail.com"
            className="contact-card email-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faEnvelope} />
            </div>
            <h3>Email</h3>
          </a>
          <a href="tel:+260777843642" className="contact-card phone-card">
            <div className="contact-icon">
              <FontAwesomeIcon icon={faPhone} />
            </div>
            <h3>Call</h3>
          </a>
          <a
            href="https://wa.me/260777843642"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card whatsapp-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faWhatsapp} />
            </div>
            <h3>WhatsApp</h3>
          </a>
          <a
            href="https://www.linkedin.com/in/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card linkedin-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faLinkedin} />
            </div>
            <h3>LinkedIn</h3>
          </a>
          <a
            href="https://github.com/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card github-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faGithub} />
            </div>
            <h3>GitHub</h3>
          </a>
          <a
            href="https://x.com/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card twitter-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faXTwitter} />
            </div>
            <h3>X</h3>
          </a>
          <a
            href="https://www.facebook.com/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card facebook-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faFacebook} />
            </div>
            <h3>Facebook</h3>
          </a>
          <a
            href="https://www.instagram.com/4anglais"
            target="_blank"
            rel="noopener noreferrer"
            className="contact-card instagram-card"
          >
            <div className="contact-icon">
              <FontAwesomeIcon icon={faInstagram} />
            </div>
            <h3>Instagram</h3>
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="page-footer">
        <div className="footer-inner">
          <div className="footer-column footer-info">
            <h3>Made by Angel</h3>
            <p className="footer-bio">
              Building scalable applications and exploring the depths of mobile
              systems.
            </p>
            <div className="footer-social-links">
              <a
                href="https://www.facebook.com/4anglais"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faFacebook} />
              </a>
              <a
                href="https://www.instagram.com/4anglais"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faInstagram} />
              </a>
              <a
                href="https://github.com/4anglais"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faGithub} />
              </a>
              <a
                href="https://www.linkedin.com/in/4anglais"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faLinkedin} />
              </a>
              <a
                href="https://x.com/4anglais"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FontAwesomeIcon icon={faXTwitter} />
              </a>
            </div>
            <p className="footer-copyright">
              © {new Date().getFullYear()} All rights reserved.
            </p>
          </div>

          <div className="footer-column footer-form-container">
            <h3>Send me a message</h3>
            <form
              className="email-form"
              action="https://formspree.io/f/mdkqrwgw"
              method="POST"
            >
              <input type="text" name="name" placeholder="Name" required />
              <input type="email" name="email" placeholder="Email" required />
              <textarea
                name="message"
                placeholder="How can I help you?"
                rows="3"
                required
              ></textarea>
              <button type="submit" className="footer-submit">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
